import { httpClient } from "@/shared/infrastructure/http/http-client";
import type {
  AuthRepositoryPort,
  AuthResult,
  AuthTokens,
  InitiateRegistrationInput,
  LoginCredentials,
  OnboardingInput,
} from "../../application/ports/auth.repository.port";

interface LoginResponse {
  accessToken: string;
  refreshToken: string;
  userId: string;
  fullName: string;
  email: string | null;
}

interface VerifyEmailResponse {
  accessToken: string;
  refreshToken: string;
  userId: string;
  email: string | null;
}

interface DoctorLoginResponse {
  accessToken: string;
  refreshToken: string;
  doctorId: string;
  name: string;
  email: string;
}

export const authHttpRepository: AuthRepositoryPort = {
  async login(credentials: LoginCredentials): Promise<AuthResult> {
    const res = await httpClient<LoginResponse>("/auth/login", {
      method: "POST",
      body: credentials,
      skipAuth: true,
    });
    return { ...res, role: "patient" };
  },

  async cancelAccountDeletionWithPassword(credentials: LoginCredentials): Promise<AuthResult> {
    const res = await httpClient<LoginResponse>("/auth/account-deletion/cancel-with-password", {
      method: "POST",
      body: credentials,
      skipAuth: true,
    });
    return { ...res, role: "patient" };
  },

  async doctorLogin(credentials: LoginCredentials): Promise<AuthResult> {
    const res = await httpClient<DoctorLoginResponse>("/auth/doctor/login", {
      method: "POST",
      body: credentials,
      skipAuth: true,
    });
    return {
      accessToken: res.accessToken,
      refreshToken: res.refreshToken,
      userId: res.doctorId,
      fullName: res.name,
      email: res.email,
      role: "doctor",
    };
  },

  async onboarding(input: OnboardingInput): Promise<AuthResult> {
    const res = await httpClient<LoginResponse>("/auth/onboarding", {
      method: "POST",
      body: input,
      skipAuth: true,
    });
    return { ...res, role: "patient" };
  },

  refresh(refreshToken: string): Promise<AuthTokens> {
    return httpClient<AuthTokens>("/auth/refresh", {
      method: "POST",
      body: { refreshToken },
      skipAuth: true,
    });
  },

  logout(refreshToken: string): Promise<void> {
    return httpClient<void>("/auth/logout", {
      method: "POST",
      body: { refreshToken },
      skipAuth: true,
    });
  },

  initiateRegistration(input: InitiateRegistrationInput): Promise<void> {
    return httpClient<void>("/auth/register/initiate", {
      method: "POST",
      body: input,
      skipAuth: true,
    });
  },

  async verifyEmail(token: string): Promise<AuthResult> {
    const res = await httpClient<VerifyEmailResponse>("/auth/verify-email", {
      method: "POST",
      body: { token },
      skipAuth: true,
    });
    return { ...res, fullName: "", role: "patient" };
  },

  async googleLogin(idToken: string): Promise<AuthResult> {
    const res = await httpClient<LoginResponse>("/auth/google", {
      method: "POST",
      body: { idToken },
      skipAuth: true,
    });
    return { ...res, role: "patient" };
  },

  setPassword(password: string): Promise<void> {
    return httpClient<void>("/auth/set-password", {
      method: "POST",
      body: { password },
    });
  },

  forgotPassword(email: string): Promise<void> {
    return httpClient<void>("/auth/forgot-password", {
      method: "POST",
      body: { email },
      skipAuth: true,
    });
  },

  resetPassword(token: string, newPassword: string): Promise<void> {
    return httpClient<void>("/auth/reset-password", {
      method: "POST",
      body: { token, newPassword },
      skipAuth: true,
    });
  },
};
